/**
 * SPCB Checkout Shipping Module v1.10.0
 *
 * Main orchestrator that handles:
 * 1. Shipping time messages based on selected shipping method
 * 2. "Por Pagar" shipping labels - replaces "Gratis" for non-pickup $0 methods
 * 3. Shipping info hints with modal (pickup/branch/home)
 * 4. User click tracking - prevents modals from auto-opening on page load
 * 5. SPCB-only shipping sync (no interference with third-party methods)
 * 6. Coordination of "otro" modal and validation
 *
 * Sub-modules:
 * - shipping/other-modal.js - "Otro" transport modal
 * - shipping/shipping-validator.js - Checkout validation
 *
 * @package SuperPymeCheckoutBlocks
 */

(function() {
    'use strict';

    // Debug mode
    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB Shipping]', ...args);
    const SHIPPING_SYNC_ENABLED = window.spcbConfig?.enableShippingSync === true;

    // Get REGION_NAMES from document module or define locally
    const REGION_NAMES = window.spcbRegionNames || {
        'CL-AP': 'Arica y Parinacota',
        'CL-TA': 'Tarapacá',
        'CL-AN': 'Antofagasta',
        'CL-AT': 'Atacama',
        'CL-CO': 'Coquimbo',
        'CL-VS': 'Valparaíso',
        'CL-RM': 'Región Metropolitana',
        'CL-LI': 'O\'Higgins',
        'CL-ML': 'Maule',
        'CL-NB': 'Ñuble',
        'CL-BI': 'Biobío',
        'CL-AR': 'La Araucanía',
        'CL-LR': 'Los Ríos',
        'CL-LL': 'Los Lagos',
        'CL-AI': 'Aysén',
        'CL-MA': 'Magallanes'
    };

    // SVG Icons (Lucide) - replaces emojis for better consistency
    const ICONS = {
        truck: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spcb-icon"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>',
        mapPin: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spcb-icon"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>',
        clock: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spcb-icon"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
        alertTriangle: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spcb-icon"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>',
        mail: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spcb-icon"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>',
        package: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spcb-icon"><path d="m16.5 9.4-9-5.19"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" x2="12" y1="22" y2="12"/></svg>',
        home: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spcb-icon"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>'
    };

    // ============================================
    // USER INTERACTION TRACKING
    // ============================================
    let userClickedShipping = false;

    // ============================================
    // SHARED UTILITIES FOR SUB-MODULES
    // ============================================
    window.spcbShippingUtils = {
        log,
        ICONS,
        REGION_NAMES,
        get userClickedShipping() { return userClickedShipping; },
        set userClickedShipping(val) { userClickedShipping = val; },
        showShippingPrompt
    };

    // ============================================
    // SHIPPING TIME MESSAGES
    // ============================================

    let currentShippingTimeElement = null;

    function getShippingType() {
        const selectedRadio = document.querySelector('.wc-block-components-shipping-rates-control input[type="radio"]:checked');
        if (!selectedRadio) {
            log('getShippingType: No radio selected yet');
            return null;
        }

        const rateId = selectedRadio.value;
        const config = window.spcbConfig || {};

        // Check if this is the store's own delivery method (configured in admin)
        const storeMethodId = config.storeDelivery?.methodId;
        if (storeMethodId) {
            log('getShippingType: comparing rateId:', rateId, 'with storeMethodId:', storeMethodId);
            if (rateId === storeMethodId) {
                return 'store_delivery';
            }
        }

        const label = selectedRadio.closest('label');
        const labelText = label?.querySelector('.wc-block-components-radio-control__label')?.textContent?.toLowerCase() || '';

        if (labelText.includes('retiro') || labelText.includes('pickup') || labelText.includes('tienda')) {
            return 'pickup';
        } else if (labelText.includes('sucursal')) {
            return 'branch';
        } else if (labelText.includes('otro')) {
            return 'other';
        } else {
            return 'home';
        }
    }

    function showShippingTimeMessage() {
        // Remove existing message
        if (currentShippingTimeElement) {
            currentShippingTimeElement.remove();
            currentShippingTimeElement = null;
        }

        const config = window.spcbConfig || {};
        const shippingTimes = config.shippingTimes;
        if (!shippingTimes) {
            log('No shipping times configured');
            return;
        }

        const shippingType = getShippingType();
        if (!shippingType) {
            log('No shipping type detected');
            return;
        }

        // Get the appropriate message based on shipping type
        let message;
        if (shippingType === 'store_delivery') {
            // Use the custom store delivery message
            message = config.storeDelivery?.message || shippingTimes.home;
        } else {
            message = shippingTimes[shippingType];
        }

        if (!message) {
            log('No message for shipping type:', shippingType);
            return;
        }

        // Find the shipping options title
        const shippingTitle = document.querySelector('.wc-block-components-shipping-rates-control')?.closest('.wc-block-checkout__shipping-option')?.querySelector('.wc-block-components-checkout-step__title');

        // Alternative: find by heading text
        const allTitles = document.querySelectorAll('.wc-block-components-title, .wc-block-components-checkout-step__title');
        let targetTitle = shippingTitle;

        if (!targetTitle) {
            for (const title of allTitles) {
                if (title.textContent?.toLowerCase().includes('opciones de env') ||
                    title.textContent?.toLowerCase().includes('shipping')) {
                    targetTitle = title;
                    break;
                }
            }
        }

        if (!targetTitle) {
            // Fallback: insert after shipping rates control
            const shippingRates = document.querySelector('.wc-block-components-shipping-rates-control');
            if (shippingRates) {
                targetTitle = shippingRates;
            }
        }

        if (!targetTitle) {
            log('Shipping title not found for time message');
            return;
        }

        // Create time message element
        const timeMessage = document.createElement('div');
        timeMessage.className = 'spcb-shipping-time-message';
        timeMessage.innerHTML = `
            <span class="spcb-time-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
            </span>
            <span class="spcb-time-text">${message}</span>
        `;

        // Insert after the title
        targetTitle.insertAdjacentElement('afterend', timeMessage);
        currentShippingTimeElement = timeMessage;

        log('Shipping time message shown:', shippingType, message);
    }

    function handleShippingChangeForTime() {
        // Debounce to avoid multiple calls
        clearTimeout(window.spcbShippingTimeDebounce);
        window.spcbShippingTimeDebounce = setTimeout(showShippingTimeMessage, 100);
    }

    // ============================================
    // "GRATIS" → "POR PAGAR" REPLACEMENT
    // ============================================

    function isPickupModeActive() {
        const selectedMethod = document.querySelector('.wc-block-checkout__shipping-method-option--selected, .wc-block-checkout__shipping-method-option[aria-checked="true"]');
        if (selectedMethod) {
            const title = selectedMethod.querySelector('.wc-block-checkout__shipping-method-option-title');
            if (title) {
                const text = (title.textContent || '').toLowerCase();
                if (text.includes('retiro') || text.includes('pickup') || text.includes('tienda')) {
                    return true;
                }
            }
        }
        return false;
    }

    function isPickupMethod(option) {
        const labelEl = option.querySelector('.wc-block-components-radio-control__label');
        if (!labelEl) return false;

        const labelText = (labelEl.textContent || '').toLowerCase();
        return labelText.includes('retiro') ||
               labelText.includes('pickup') ||
               labelText.includes('tienda') ||
               labelText.includes('local pickup');
    }

    function replaceGratisWithPorPagar() {
        if (isPickupModeActive()) {
            log('Pickup mode active, skipping Por Pagar replacement');
            return;
        }

        // Get methods marked as "real free shipping" from settings
        const freeShippingMethods = window.spcbConfig?.freeShippingMethods || [];
        log('Free shipping methods from settings:', freeShippingMethods);

        // 1. Replace in shipping rate options
        const options = document.querySelectorAll('.wc-block-components-radio-control__option');
        options.forEach(option => {
            if (isPickupMethod(option)) {
                return;
            }

            // Get the rate_id from the radio input
            const radioInput = option.querySelector('input[type="radio"]');
            const rateId = radioInput?.value || '';

            // Skip if this method is marked as "real free shipping" in settings
            if (freeShippingMethods.includes(rateId)) {
                log('Skipping "Por Pagar" for free shipping method:', rateId);
                return;
            }

            const gratisSpan = option.querySelector('.wc-block-checkout__shipping-option--free');
            if (gratisSpan && gratisSpan.textContent === 'Gratis') {
                gratisSpan.textContent = 'Por Pagar';
                log('Changed "Gratis" to "Por Pagar" for option:', option.querySelector('.wc-block-components-radio-control__label')?.textContent);
            }
        });

        // 2. Replace in order summary totals (only if selected method is NOT a free shipping method)
        const selectedRadio = document.querySelector('.wc-block-components-shipping-rates-control input[type="radio"]:checked');
        const selectedRateId = selectedRadio?.value || '';

        // Skip summary replacement if selected method is a real free shipping method
        if (freeShippingMethods.includes(selectedRateId)) {
            log('Skipping summary "Por Pagar" - selected method is free shipping:', selectedRateId);
            return;
        }

        const shippingTotals = document.querySelectorAll('.wc-block-components-totals-shipping .wc-block-components-totals-item__value');
        shippingTotals.forEach(valueEl => {
            const item = valueEl.closest('.wc-block-components-totals-item');
            const labelEl = item?.querySelector('.wc-block-components-totals-item__label');
            const labelText = (labelEl?.textContent || '').toLowerCase();

            if (labelText.includes('retiro') || labelText.includes('pickup') || labelText.includes('tienda')) {
                return;
            }

            const strongEl = valueEl.querySelector('strong');
            if (strongEl && strongEl.textContent === 'Gratis') {
                strongEl.textContent = 'Por Pagar';
                log('Changed "Gratis" to "Por Pagar" in order summary');
            }
            if (valueEl.textContent.trim() === 'Gratis') {
                valueEl.textContent = 'Por Pagar';
            }
        });
    }

    function forceShippingSyncOnLoad() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('forceShippingSyncOnLoad disabled (official flow)');
            return;
        }

        // v1.12.0: Skip if checkout is processing to prevent race conditions
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            log('Checkout processing - skipping forceShippingSyncOnLoad');
            return;
        }

        // v1.18.3: Skip if branch-checkbox is handling refresh
        if (window.spcbBranchRefreshInProgress) {
            log('Branch refresh in progress - skipping forceShippingSyncOnLoad');
            return;
        }

        const selectedRadio = document.querySelector('.wc-block-components-shipping-rates-control input[type="radio"]:checked');
        if (!selectedRadio) {
            log('No shipping radio selected, skipping sync');
            return;
        }

        const rateId = selectedRadio.value;

        // ONLY sync SPCB methods - third-party methods are handled natively by WC Blocks
        const isSpcbMethod = rateId && rateId.startsWith('spcb_');
        if (!isSpcbMethod) {
            log('Skipping sync for non-SPCB method:', rateId, '(WC Blocks handles this natively)');
            return;
        }

        const summaryValue = document.querySelector('.wc-block-components-totals-shipping .wc-block-components-totals-item__value');
        const summaryText = summaryValue?.textContent || '';
        const hasStaleData = summaryText.includes('No hay ninguna opción') ||
                            summaryText.includes('No shipping options') ||
                            summaryText.includes('necesita');

        if (rateId) {
            const selectedLabel = selectedRadio.closest('label')?.querySelector('.wc-block-components-radio-control__label')?.textContent;
            log('Forcing shipping sync for:', selectedLabel, '| Rate ID:', rateId, '| Reason:', hasStaleData ? 'stale data' : 'SPCB method');

            try {
                const wpData = window.wp?.data;
                if (wpData?.dispatch && wpData?.select) {
                    log('Attempting to select rate via wp.data:', rateId);

                    const cartStore = wpData.dispatch('wc/store/cart');
                    if (cartStore?.selectShippingRate) {
                        cartStore.selectShippingRate(rateId, 0);
                        log('selectShippingRate dispatched for:', rateId);
                    }

                    // REMOVIDO - La invalidación causa race conditions con React hydration
                    // selectShippingRate() ya es suficiente para actualizar el store
                    // if (cartStore?.invalidateResolutionForStore) {
                    //     cartStore.invalidateResolutionForStore();
                    //     log('Cart store invalidated');
                    // }
                }
            } catch (e) {
                log('wp.data method failed:', e.message);
            }

            selectedRadio.dispatchEvent(new Event('change', { bubbles: true }));

            setTimeout(() => {
                // v1.14.13: Re-verify checkout state before executing delayed sync
                // This prevents race conditions if checkout started during the 300ms delay
                if (window.spcbCheckoutLock?.isProcessing?.()) {
                    log('Retry sync cancelled - checkout processing started during delay');
                    return;
                }

                // v1.18.3: Also check if branch-checkbox is handling refresh
                if (window.spcbBranchRefreshInProgress) {
                    log('Retry sync cancelled - branch refresh in progress');
                    return;
                }

                const newSummaryText = document.querySelector('.wc-block-components-totals-shipping .wc-block-components-totals-item__value')?.textContent || '';
                if (newSummaryText.includes('No hay ninguna opción') || newSummaryText.includes('necesita')) {
                    log('Still showing error, re-dispatching Store API sync...');
                    try {
                        const wpData = window.wp?.data;
                        const cartStore = wpData?.dispatch?.('wc/store/cart');
                        if (cartStore?.selectShippingRate) {
                            cartStore.selectShippingRate(rateId, 0);
                        }
                    } catch (e) {
                        log('Retry sync failed:', e.message);
                    }
                    selectedRadio.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }, 300);
        }
    }

    function ensureShippingRateSync() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('ensureShippingRateSync disabled (official flow)');
            return;
        }

        // v1.12.0: Skip if checkout is processing to prevent race conditions
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            return;
        }

        // v1.18.3: Skip if branch-checkbox is handling refresh
        if (window.spcbBranchRefreshInProgress) {
            return;
        }

        const selectedRadio = document.querySelector('.wc-block-components-shipping-rates-control input[type="radio"]:checked');
        if (!selectedRadio) return;

        const rateId = selectedRadio.value;
        if (!rateId || !rateId.startsWith('spcb_')) return;

        try {
            const wpData = window.wp?.data;
            if (!wpData?.select || !wpData?.dispatch) return;

            const cartStore = wpData.select('wc/store/cart');
            const shippingRates = cartStore?.getShippingRates?.();

            if (shippingRates && shippingRates.length > 0) {
                const packageRates = shippingRates[0]?.shipping_rates || [];
                const currentSelected = packageRates.find(r => r.selected);

                if (!currentSelected || currentSelected.rate_id !== rateId) {
                    log('Store rate mismatch! DOM:', rateId, 'Store:', currentSelected?.rate_id);
                    const cartDispatch = wpData.dispatch('wc/store/cart');
                    if (cartDispatch?.selectShippingRate) {
                        cartDispatch.selectShippingRate(rateId, 0);
                        log('Forced rate selection:', rateId);
                    }
                }
            }
        } catch (e) {
            log('ensureShippingRateSync error:', e.message);
        }
    }

    function initShippingPriceObserver() {
        // Initial replacement
        replaceGratisWithPorPagar();

        // Re-check when shipping rates container changes
        const shippingContainer = document.querySelector('.wc-block-components-shipping-rates-control');
        if (shippingContainer) {
            const observer = new MutationObserver(() => {
                clearTimeout(window.spcbPorPagarDebounce);
                window.spcbPorPagarDebounce = setTimeout(replaceGratisWithPorPagar, 100);
            });

            observer.observe(shippingContainer, {
                childList: true,
                subtree: true,
                characterData: true
            });
        }

        // Also observe the order summary for changes
        const orderSummary = document.querySelector('.wc-block-components-totals-shipping, .wp-block-woocommerce-checkout-order-summary-block');
        if (orderSummary) {
            const summaryObserver = new MutationObserver(() => {
                clearTimeout(window.spcbSummaryDebounce);
                window.spcbSummaryDebounce = setTimeout(replaceGratisWithPorPagar, 100);
            });

            summaryObserver.observe(orderSummary, {
                childList: true,
                subtree: true,
                characterData: true
            });
        }

        // Listen for clicks on shipping method selector
        document.addEventListener('click', (e) => {
            if (e.target.closest('.wc-block-checkout__shipping-method-option')) {
                setTimeout(replaceGratisWithPorPagar, 200);
                setTimeout(replaceGratisWithPorPagar, 500);
                setTimeout(replaceGratisWithPorPagar, 1000);
            }
        });

        setTimeout(replaceGratisWithPorPagar, 500);
        setTimeout(replaceGratisWithPorPagar, 1500);
        setTimeout(replaceGratisWithPorPagar, 3000);

        log('Por Pagar shipping observer initialized');
    }

    // ============================================
    // SHIPPING INFO HINTS + MODAL
    // ============================================

    let shippingInfoModalCreated = false;

    function getDeliveryMethodType() {
        const pickupOption = document.querySelector('.wc-block-checkout__shipping-method-option[aria-checked="true"]');
        if (pickupOption) {
            const title = pickupOption.querySelector('.wc-block-checkout__shipping-method-option-title');
            if (title && title.textContent.toLowerCase().includes('retiro')) {
                return 'pickup';
            }
        }

        const selectedMethod = document.querySelector('.wc-block-checkout__shipping-method-option--selected');
        if (selectedMethod) {
            const title = selectedMethod.querySelector('.wc-block-checkout__shipping-method-option-title');
            if (title) {
                const text = title.textContent.toLowerCase();
                if (text.includes('retiro')) return 'pickup';
                if (text.includes('enviar') || text.includes('ship')) return 'shipping';
            }
        }

        return 'shipping';
    }

    function getDetailedShippingType() {
        const deliveryMethod = getDeliveryMethodType();
        if (deliveryMethod === 'pickup') return 'pickup';

        const shippingType = getShippingType();
        return shippingType || 'home';
    }

    function updateShippingInfoHint() {
        const existingHint = document.querySelector('.spcb-shipping-info-hint');
        if (existingHint) existingHint.remove();

        const methodContainer = document.querySelector('.wc-block-checkout__shipping-method-container');
        if (!methodContainer) {
            log('Shipping method container not found for hint');
            return;
        }

        const deliveryType = getDetailedShippingType();
        log('Delivery type for hint:', deliveryType);

        const config = window.spcbConfig || {};
        const messages = config.shippingMessages || {};

        const hint = document.createElement('div');
        hint.className = 'spcb-shipping-info-hint';

        let hintText = '';
        let modalType = '';

        if (deliveryType === 'pickup') {
            hintText = messages.pickupHint || 'Espera confirmación por email antes de retirar.';
            modalType = 'pickup';
        } else if (deliveryType === 'branch') {
            hintText = messages.shippingHint || 'Recibirás un email con código de seguimiento.';
            modalType = 'branch';
        } else if (deliveryType === 'other') {
            hintText = messages.otherHint || 'Coordinaremos el retiro con tu transporte.';
            modalType = 'other';
        } else {
            hintText = messages.shippingHint || 'Recibirás un email con código de seguimiento.';
            modalType = 'home';
        }

        hint.innerHTML = `
            <svg class="spcb-info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span class="spcb-info-text">${hintText}</span>
            <button type="button" class="spcb-info-link" data-modal-type="${modalType}">Más info</button>
        `;

        hint.querySelector('.spcb-info-link').addEventListener('click', (e) => {
            e.preventDefault();
            openShippingInfoModal(e.target.dataset.modalType);
        });

        methodContainer.insertAdjacentElement('afterend', hint);
        log('Shipping info hint injected:', deliveryType);
    }

    function createShippingInfoModal() {
        if (shippingInfoModalCreated) return;

        const modalHTML = `
            <div id="spcb-shipping-info-modal" class="spcb-modal" style="display:none;">
                <div class="spcb-modal-overlay" data-close="true"></div>
                <div class="spcb-modal-content spcb-info-modal-content">
                    <div class="spcb-modal-header">
                        <h3 id="spcb-info-modal-title">Información de envío</h3>
                        <button type="button" class="spcb-modal-close" aria-label="Cerrar">&times;</button>
                    </div>
                    <div class="spcb-modal-body" id="spcb-info-modal-body">
                    </div>
                    <div class="spcb-modal-footer">
                        <button type="button" class="spcb-modal-confirm spcb-info-modal-ok">Entendido</button>
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);
        shippingInfoModalCreated = true;

        const modal = document.getElementById('spcb-shipping-info-modal');
        modal.querySelector('.spcb-modal-close').addEventListener('click', closeShippingInfoModal);
        modal.querySelector('.spcb-modal-overlay').addEventListener('click', closeShippingInfoModal);
        modal.querySelector('.spcb-info-modal-ok').addEventListener('click', closeShippingInfoModal);

        log('Shipping info modal created');
    }

    function textToHtml(text) {
        if (!text) return '';
        return text.split('\n').map(line => `<p>${line}</p>`).join('');
    }

    function openShippingInfoModal(type) {
        createShippingInfoModal();

        const modal = document.getElementById('spcb-shipping-info-modal');
        const titleEl = document.getElementById('spcb-info-modal-title');
        const bodyEl = document.getElementById('spcb-info-modal-body');

        const config = window.spcbConfig || {};
        const shippingTimes = config.shippingTimes || {};
        const messages = config.shippingMessages || {};

        let title = '';
        let content = '';

        if (type === 'pickup') {
            title = `${ICONS.mapPin} Retiro en Tienda`;
            const warningText = messages.pickupWarning || "Espera el correo de confirmación antes de ir a retirar tu pedido.\nSi no recibes el correo, tu pedido aún no estará listo.";
            content = `
                <div class="spcb-info-section">
                    <h4>${ICONS.clock} Tiempo de retiro</h4>
                    <p>Tu pedido estará listo en máximo <strong>${shippingTimes.pickup || '24 horas hábiles'}</strong>.</p>
                </div>
                <div class="spcb-info-section spcb-info-warning">
                    <h4>${ICONS.alertTriangle} Aviso importante</h4>
                    ${textToHtml(warningText)}
                </div>
            `;
        } else if (type === 'branch') {
            title = `${ICONS.package} Envío a Sucursal`;
            const confirmationText = messages.branchConfirmation || "Tu pedido será enviado a la sucursal que elegiste para que puedas retirarlo allí.\nRecibirás un correo electrónico con la confirmación apenas tu pedido sea despachado, incluyendo un código de seguimiento.";
            content = `
                <div class="spcb-info-section">
                    <h4>${ICONS.mail} Confirmación de envío</h4>
                    ${textToHtml(confirmationText)}
                </div>
                <div class="spcb-info-section">
                    <h4>${ICONS.clock} Tiempo de procesamiento</h4>
                    <p>El tiempo estimado para preparar y despachar tu pedido es de <strong>${shippingTimes.branch || '24 a 48 horas hábiles'}</strong>.</p>
                    <p>Luego, el tiempo de llegada dependerá de la empresa de transporte seleccionada.</p>
                </div>
            `;
        } else if (type === 'other') {
            title = `${ICONS.truck} Transporte Propio`;
            const confirmationText = messages.otherConfirmation || "Tu pedido será preparado para que tu transporte lo retire en nuestra tienda.\nTe contactaremos cuando esté listo para coordinar el retiro.";
            content = `
                <div class="spcb-info-section">
                    <h4>${ICONS.package} Cómo funciona</h4>
                    ${textToHtml(confirmationText)}
                </div>
                <div class="spcb-info-section">
                    <h4>${ICONS.clock} Tiempo de preparación</h4>
                    <p>El tiempo estimado para preparar tu pedido es de <strong>${shippingTimes.other || shippingTimes.pickup || '24 horas hábiles'}</strong>.</p>
                </div>
                <div class="spcb-info-section spcb-info-warning">
                    <h4>${ICONS.alertTriangle} Importante</h4>
                    <p>Asegúrate de indicar el nombre de la empresa de transporte en el campo correspondiente.</p>
                    <p>Tu transporte debe presentar el número de pedido al momento del retiro.</p>
                </div>
            `;
        } else {
            title = `${ICONS.truck} Envío a Domicilio`;
            const confirmationText = messages.homeConfirmation || "Tu pedido será enviado a la dirección que indicaste.\nRecibirás un correo electrónico con la confirmación apenas tu pedido sea despachado, incluyendo un código de seguimiento.";
            content = `
                <div class="spcb-info-section">
                    <h4>${ICONS.mail} Confirmación de envío</h4>
                    ${textToHtml(confirmationText)}
                </div>
                <div class="spcb-info-section">
                    <h4>${ICONS.clock} Tiempo de procesamiento</h4>
                    <p>${shippingTimes.home || 'El tiempo de entrega dependerá del método de envío seleccionado.'}</p>
                </div>
            `;
        }

        titleEl.innerHTML = title;
        bodyEl.innerHTML = content;

        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        log('Shipping info modal opened:', type);
    }

    function closeShippingInfoModal() {
        const modal = document.getElementById('spcb-shipping-info-modal');
        if (modal) {
            modal.style.display = 'none';
            document.body.style.overflow = '';
        }
        log('Shipping info modal closed');
    }

    /**
     * Show delivery method hint below "Entrega" title
     */
    function showDeliveryMethodHint() {
        const config = window.spcbConfig || {};
        const hintText = config.shippingMessages?.deliveryMethodHint;

        // Don't show if empty or already exists
        if (!hintText || document.querySelector('.spcb-delivery-method-hint')) {
            return;
        }

        // Find the shipping method section title
        const shippingMethodSection = document.getElementById('shipping-method');
        if (!shippingMethodSection) return;

        const title = shippingMethodSection.querySelector('.wc-block-components-checkout-step__title');
        if (!title) return;

        // Create hint element
        const hint = document.createElement('p');
        hint.className = 'spcb-delivery-method-hint';
        hint.textContent = hintText;
        hint.style.cssText = 'margin: -14px 0 10px 0; font-size: 13px; color: #666;';

        // Insert after heading container
        const headingContainer = title.closest('.wc-block-components-checkout-step__heading');
        if (headingContainer) {
            headingContainer.insertAdjacentElement('afterend', hint);
            log('Delivery method hint shown');
        }
    }

    function initShippingInfoHints() {
        setTimeout(updateShippingInfoHint, 200);
        setTimeout(showShippingTimeMessage, 300); // Initial time message
        setTimeout(showDeliveryMethodHint, 400); // Delivery method hint

        document.addEventListener('click', (e) => {
            if (e.target.closest('.wc-block-checkout__shipping-method-option')) {
                // Update both hint and time message when switching Enviar/Retiro
                setTimeout(updateShippingInfoHint, 150);
                setTimeout(updateShippingInfoHint, 500);
                setTimeout(showShippingTimeMessage, 200);
                setTimeout(showShippingTimeMessage, 600);
            }
        });

        document.addEventListener('change', (e) => {
            if (e.target.type === 'radio' && e.target.closest('.wc-block-components-shipping-rates-control')) {
                setTimeout(updateShippingInfoHint, 100);
                setTimeout(showShippingTimeMessage, 150);
            }
        });

        log('Shipping info hints initialized');
    }

    // ============================================
    // SHIPPING PROMPT
    // ============================================

    function showShippingPrompt(type) {
        removeShippingPrompt();

        const shippingControl = document.querySelector('.wc-block-components-shipping-rates-control');
        if (!shippingControl) return;

        const prompt = document.createElement('div');
        prompt.className = 'spcb-shipping-prompt';
        prompt.dataset.type = type;

        const message = type === 'other'
            ? 'Haz clic en "Otro" para ingresar el nombre del transporte'
            : '';

        if (!message) {
            return;
        }

        prompt.innerHTML = `
            <div style="
                background: #fff3cd;
                border: 1px solid #ffc107;
                border-radius: 6px;
                padding: 10px 12px;
                margin-top: 12px;
                margin-bottom: 8px;
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 13px;
                color: #856404;
            ">
                <span style="font-size: 16px;">👆</span>
                <span>${message}</span>
            </div>
        `;

        shippingControl.parentNode.insertBefore(prompt, shippingControl.nextSibling);
        log('Shipping prompt shown for:', type);
    }

    function removeShippingPrompt() {
        document.querySelectorAll('.spcb-shipping-prompt').forEach(el => el.remove());
    }

    let promptCheckerInterval = null;

    function startPromptChecker() {
        if (promptCheckerInterval) return;

        // Defer loading of module functions
        const getOtherData = () => window.spcbShipping?.getOtherData?.() || null;
        const isOtherShippingSelected = () => window.spcbShipping?.isOtherShippingSelected?.() || false;

        promptCheckerInterval = setInterval(() => {
            // v1.12.0: Skip if checkout is processing
            if (window.spcbCheckoutLock?.isProcessing?.()) {
                return;
            }

            const config = window.spcbConfig || {};

            if (userClickedShipping) {
                removeShippingPrompt();
                return;
            }

            if (isOtherShippingSelected() && config.otherShippingEnabled) {
                const existingData = getOtherData();
                if ((!existingData || !existingData.transportName) && !document.querySelector('.spcb-shipping-prompt')) {
                    log('Prompt checker: Adding other prompt');
                    showShippingPrompt('other');
                }
            }
            else {
                if (document.querySelector('.spcb-shipping-prompt')) {
                    removeShippingPrompt();
                }
            }
        }, 1000);

        log('Prompt checker started');
    }

    function stopPromptChecker() {
        if (promptCheckerInterval) {
            clearInterval(promptCheckerInterval);
            promptCheckerInterval = null;
            log('Prompt checker stopped');
        }
    }

    // ============================================
    // MAIN SHIPPING HANDLER
    // ============================================

    function handleShippingChange() {
        // v1.12.0: Skip if checkout is processing to prevent race conditions
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            return;
        }

        const config = window.spcbConfig || {};

        // Defer loading of module functions
        const getOtherData = () => window.spcbShipping?.getOtherData?.() || null;
        const isOtherShippingSelected = () => window.spcbShipping?.isOtherShippingSelected?.() || false;
        const showOtherSummary = (data) => window.spcbShipping?.showOtherSummary?.(data);
        const removeOtherSummary = () => window.spcbShipping?.removeOtherSummary?.();
        const openOtherModal = () => window.spcbShipping?.openOtherModal?.();
        const saveOtherDataToCookie = (data) => window.spcbShipping?.saveOtherDataToCookie?.(data);
        const updateOtherHiddenField = (data) => window.spcbShipping?.updateOtherHiddenField?.(data);

        // Handle "Otro" Shipping
        if (isOtherShippingSelected() && config.otherShippingEnabled) {
            document.body.classList.add('spcb-other-mode');

            const existingData = getOtherData();

            if (existingData && existingData.transportName) {
                showOtherSummary(existingData);
                updateOtherHiddenField(existingData);
                saveOtherDataToCookie(existingData);
            } else if (userClickedShipping) {
                log('Opening other modal - user clicked shipping');
                setTimeout(openOtherModal, 50);
            } else {
                log('Other shipping auto-selected without data - NOT opening modal');
                showShippingPrompt('other');
            }
        }
        // Neither branch nor other
        else {
            document.body.classList.remove('spcb-other-mode');
            removeOtherSummary();
            removeShippingPrompt();
        }
    }

    // ============================================
    // BRANCH SHIPPING INITIALIZATION
    // ============================================

    let branchShippingInitialized = false;
    let shippingMethodObserver = null;

    function initBranchShipping() {
        if (branchShippingInitialized) return;
        branchShippingInitialized = true;

        // Defer loading of module functions
        const getOtherData = () => window.spcbShipping?.getOtherData?.() || null;
        const isOtherShippingSelected = () => window.spcbShipping?.isOtherShippingSelected?.() || false;
        const showOtherSummary = (data) => window.spcbShipping?.showOtherSummary?.(data);
        const openOtherModal = () => window.spcbShipping?.openOtherModal?.();
        const saveOtherDataToCookie = (data) => window.spcbShipping?.saveOtherDataToCookie?.(data);

        // Initial check with multiple delays
        setTimeout(handleShippingChange, 100);
        setTimeout(handleShippingChange, 500);
        setTimeout(handleShippingChange, 1000);
        setTimeout(handleShippingChange, 2000);

        // Listen for shipping radio changes
        document.addEventListener('change', (e) => {
            if (e.target.type === 'radio' && e.target.closest('.wc-block-components-shipping-rates-control')) {
                userClickedShipping = true;
                log('User clicked shipping radio - userClickedShipping = true');
                removeShippingPrompt();
                handleShippingChange();
            }
        });

        // Listen for clicks on shipping rate labels
        document.addEventListener('click', (e) => {
            const shippingLabel = e.target.closest('.wc-block-components-shipping-rates-control label');
            if (shippingLabel) {
                userClickedShipping = true;
                log('User clicked shipping label - userClickedShipping = true');
                removeShippingPrompt();

                const radio = shippingLabel.querySelector('input[type="radio"]');
                if (radio && radio.checked) {
                    const methodId = radio.value || '';
                    const config = window.spcbConfig || {};

                    if (methodId.startsWith('spcb_other_shipping') && config.otherShippingEnabled) {
                        const existingData = getOtherData();
                        if (!existingData || !existingData.transportName) {
                            log('Clicked already-selected other shipping without data - opening modal');
                            setTimeout(openOtherModal, 50);
                        }
                    }
                }
            }
        }, true);

        // Listen for clicks on shipping method options (Enviar / Retiro en tienda)
        document.addEventListener('click', (e) => {
            const methodOption = e.target.closest('.wc-block-checkout__shipping-method-option');
            if (methodOption) {
                userClickedShipping = false;
                log('Shipping method type changed - userClickedShipping = false');
                setTimeout(handleShippingChange, 150);
                setTimeout(handleShippingChange, 500);
                log('Shipping method option clicked');
            }
        });

        // REMOVED in v1.10.17: Address change listener was causing interference with
        // WooCommerce Blocks' native delivery method selection (Enviar vs Retiro).
        // When region/comuna changed, this code would call handleShippingChange() 3 times
        // and reset userClickedShipping, which could trigger unwanted method switches.
        // WC Blocks handles address changes natively - we don't need to interfere.

        let lastShippingRadioIds = '';

        // Observe shipping rates container
        const observeShippingRates = () => {
            const shippingContainer = document.querySelector('.wc-block-components-shipping-rates-control');
            if (!shippingContainer || shippingMethodObserver) return;

            shippingMethodObserver = new MutationObserver((mutations) => {
                // v1.12.0: Skip if checkout is processing
                if (window.spcbCheckoutLock?.isProcessing?.()) {
                    return;
                }

                clearTimeout(window.spcbBranchCheckDebounce);
                window.spcbBranchCheckDebounce = setTimeout(() => {
                    checkAndShowShippingUI();
                }, 100);

                clearTimeout(window.spcbBranchCheckDebounce2);
                window.spcbBranchCheckDebounce2 = setTimeout(() => {
                    checkAndShowShippingUI();
                }, 500);

                clearTimeout(window.spcbBranchCheckDebounce3);
                window.spcbBranchCheckDebounce3 = setTimeout(() => {
                    checkAndShowShippingUI();
                }, 1000);
            });

            function checkAndShowShippingUI() {
                const config = window.spcbConfig || {};

                const currentRadios = document.querySelectorAll('.wc-block-components-shipping-rates-control input[type="radio"]');
                const currentIds = Array.from(currentRadios).map(r => r.id).join(',');

                if (currentIds && currentIds !== lastShippingRadioIds) {
                    if (lastShippingRadioIds) {
                        userClickedShipping = false;
                        log('Shipping rates replaced (address change detected) - userClickedShipping = false');
                    }
                    lastShippingRadioIds = currentIds;
                }

                if (isOtherShippingSelected() && config.otherShippingEnabled) {
                    const existingData = getOtherData();
                    if (existingData && existingData.transportName) {
                        if (!document.querySelector('.spcb-other-summary')) {
                            log('Other shipping with data - showing summary');
                            showOtherSummary(existingData);
                            saveOtherDataToCookie(existingData);
                        }
                        removeShippingPrompt();
                    } else if (!userClickedShipping) {
                        if (!document.querySelector('.spcb-shipping-prompt')) {
                            log('Other shipping auto-selected without data - showing prompt');
                            showShippingPrompt('other');
                        }
                    }
                }
            }

            shippingMethodObserver.observe(shippingContainer, {
                childList: true,
                subtree: true
            });

            log('Shipping rates observer initialized');
        };

        observeShippingRates();
        setTimeout(observeShippingRates, 500);
        setTimeout(observeShippingRates, 1500);

        // Start prompt checker
        if (document.body.classList.contains('woocommerce-checkout')) {
            setTimeout(startPromptChecker, 2000);
        }

        log('Branch shipping initialized');
    }

    // ============================================
    // FIELD LABELS INITIALIZATION
    // ============================================

    function initShippingFieldLabels() {
        // Will be loaded from validator module
        if (window.spcbShipping?.initShippingFieldLabels) {
            window.spcbShipping.initShippingFieldLabels();
        }
    }

    // Initialize label cleanup as early as possible
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            setTimeout(initShippingFieldLabels, 100);
        });
    } else {
        setTimeout(initShippingFieldLabels, 100);
    }

    // Also run when window loads (ensures all React hydration is complete)
    window.addEventListener('load', () => {
        setTimeout(initShippingFieldLabels, 100);
        setTimeout(initShippingFieldLabels, 500);
    });

    // ============================================
    // EXPOSE MODULE
    // ============================================

    window.spcbShipping = {
        // Shipping core
        getShippingType,
        getDetailedShippingType,
        showShippingTimeMessage,
        handleShippingChangeForTime,
        isPickupModeActive,
        isPickupMethod,
        replaceGratisWithPorPagar,
        forceShippingSyncOnLoad,
        ensureShippingRateSync,
        initShippingPriceObserver,
        getDeliveryMethodType,
        updateShippingInfoHint,
        initShippingInfoHints,
        openShippingInfoModal,
        closeShippingInfoModal,

        // Main handlers
        handleShippingChange,
        initBranchShipping,
        showShippingPrompt,
        removeShippingPrompt,

        // Placeholders - will be populated by sub-modules when loaded
        getOtherData: null,
        openOtherModal: null,
        closeOtherModal: null,
        showOtherSummary: null,
        removeOtherSummary: null,
        isOtherShippingSelected: null,
        saveOtherDataToCookie: null,
        updateOtherHiddenField: null,

        initCheckoutValidation: null,
        isLocalPickupModeSelected: null,
        validateShippingAddressFields: null,
        showShippingValidationError: null,
        clearShippingValidationErrors: null,
        initShippingFieldLabels: null,
        cleanShippingFieldLabels: null,
        prefillContactFieldsFromBilling: null,
        initPrefillContactFields: null
    };

    log('SPCB Shipping Module v1.10.0 loaded (orchestrator)');
})();

// ============================================
// LOAD SUB-MODULES
// ============================================

// Import and register sub-modules
// Note: These imports work in webpack/bundler context
// For direct browser usage, the modules need to be loaded separately

import {
    isOtherShippingSelected,
    getOtherData,
    saveOtherDataToCookie,
    updateOtherHiddenField,
    createOtherModal,
    openOtherModal,
    closeOtherModal,
    showOtherSummary,
    removeOtherSummary
} from './shipping/other-modal.js';

import {
    isLocalPickupModeSelected,
    validateShippingAddressFields,
    showShippingValidationError,
    clearShippingValidationErrors,
    showBranchValidationModal,
    showOtherValidationModal,
    initCheckoutValidation,
    cleanShippingFieldLabels,
    initShippingFieldLabels,
    updateLocalPickupBodyClass,
    initLocalPickupBodyClass,
    autoFillBillingFromContact,
    initAutoFillBilling,
    prefillContactFieldsFromBilling,
    initPrefillContactFields
} from './shipping/shipping-validator.js';

// Register all exported functions to the global module
Object.assign(window.spcbShipping, {
    // Other modal
    isOtherShippingSelected,
    getOtherData,
    saveOtherDataToCookie,
    updateOtherHiddenField,
    createOtherModal,
    openOtherModal,
    closeOtherModal,
    showOtherSummary,
    removeOtherSummary,

    // Validation
    isLocalPickupModeSelected,
    validateShippingAddressFields,
    showShippingValidationError,
    clearShippingValidationErrors,
    showBranchValidationModal,
    showOtherValidationModal,
    initCheckoutValidation,
    cleanShippingFieldLabels,
    initShippingFieldLabels,

    // Local pickup body class (for billing visibility)
    updateLocalPickupBodyClass,
    initLocalPickupBodyClass,

    // Auto-fill billing from contact (for hidden billing to pass validation)
    autoFillBillingFromContact,
    initAutoFillBilling,

    // Pre-fill contact fields from billing (for returning users)
    prefillContactFieldsFromBilling,
    initPrefillContactFields
});

// Initialize field labels immediately after modules are registered
if (window.spcbShipping?.initShippingFieldLabels) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            window.spcbShipping.initShippingFieldLabels();
        });
    } else {
        window.spcbShipping.initShippingFieldLabels();
    }
}

// Initialize local pickup body class observer (for billing visibility)
if (window.spcbShipping?.initLocalPickupBodyClass) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            window.spcbShipping.initLocalPickupBodyClass();
        });
    } else {
        window.spcbShipping.initLocalPickupBodyClass();
    }
}

// Initialize auto-fill billing from contact (hidden billing still needs data)
if (window.spcbShipping?.initAutoFillBilling) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            window.spcbShipping.initAutoFillBilling();
        });
    } else {
        window.spcbShipping.initAutoFillBilling();
    }
}

// Initialize pre-fill contact fields from billing (for returning users)
if (window.spcbShipping?.initPrefillContactFields) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            window.spcbShipping.initPrefillContactFields();
        });
    } else {
        window.spcbShipping.initPrefillContactFields();
    }
}

/**
 * Fix tab order for contact fields (v1.16.20)
 *
 * SOLUCIÓN ROBUSTA: Reorganiza el DOM para que coincida con el orden visual.
 * El CSS `order` solo cambia la visualización, NO el tab order.
 * Al mover físicamente los elementos, el tab order coincide con el visual.
 */
function initContactFieldTabOrder() {
    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB TabOrder]', ...args);

    function reorderContactFields() {
        // Guard: no ejecutar durante checkout processing
        if (window.spcbCheckoutLock?.isProcessing?.()) {
            log('Skipped - checkout processing');
            return false;
        }

        // CORRECCIÓN: #contact ES el contenedor (no tiene hijos con esa clase)
        const container = document.getElementById('contact');
        if (!container) {
            log('Contact container not found');
            return false;
        }

        // Orden visual deseado (mismo que CSS order)
        const desiredOrder = [
            '.wc-block-components-address-form__spcb-contact_first_name',  // Nombre
            '.wc-block-components-address-form__spcb-contact_last_name',   // Apellido
            '.wc-block-components-address-form__email',                     // Email
            '.spcb-phone-shadow[data-shadow-for="contact-spcb-contact_phone"]', // Teléfono shadow
        ];

        let lastElement = null;
        let reordered = false;

        desiredOrder.forEach((selector) => {
            const element = container.querySelector(selector);
            if (!element) {
                log(`Element not found: ${selector}`);
                return;
            }

            if (lastElement) {
                if (element.previousElementSibling !== lastElement) {
                    lastElement.after(element);
                    reordered = true;
                    log(`Moved ${selector}`);
                }
            }
            lastElement = element;
        });

        // Excluir flag button del tab order
        const flagButtons = container.querySelectorAll('.iti__selected-country');
        flagButtons.forEach(btn => btn.setAttribute('tabindex', '-1'));

        if (reordered) {
            log('Contact fields DOM reordered');
        } else {
            log('Contact fields already in correct order');
        }

        return true;
    }

    reorderContactFields();
    [100, 300, 500, 1000, 2000].forEach(delay => {
        setTimeout(reorderContactFields, delay);
    });

    // Observer con guard
    const observer = new MutationObserver(() => {
        if (window.spcbCheckoutLock?.isProcessing?.()) return;
        clearTimeout(observer._timeout);
        observer._timeout = setTimeout(reorderContactFields, 100);
    });

    const contactSection = document.getElementById('contact');
    if (contactSection) {
        observer.observe(contactSection, { childList: true, subtree: true });
    }
}

// Initialize tab order
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactFieldTabOrder);
} else {
    initContactFieldTabOrder();
}

console.log('[SPCB Shipping] All sub-modules registered');

// ============================================
// HIDE DUPLICATE SHIPPING FIELDS (v1.11.5)
// ============================================
// JavaScript backup to hide duplicate shipping name/phone fields
// This runs in case CSS is overridden by themes

function hideDuplicateShippingFields() {
    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB Field Hide]', ...args);

    const fieldsToHide = ['shipping-first_name', 'shipping-last_name', 'shipping-phone'];

    function hideFields() {
        fieldsToHide.forEach(fieldId => {
            const input = document.getElementById(fieldId);
            if (!input) return;

            const wrapper = input.closest(
                '.wc-block-components-text-input, ' +
                '[class*="address-form__first_name"], ' +
                '[class*="address-form__last_name"], ' +
                '[class*="address-form__phone"]'
            );

            if (wrapper) {
                const computedStyle = window.getComputedStyle(wrapper);
                if (computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden') {
                    wrapper.style.cssText = 'display:none!important;visibility:hidden!important;position:absolute!important;left:-9999px!important;height:0!important;width:0!important;overflow:hidden!important;';
                    log('JS hidden field:', fieldId);
                }
            }
        });
    }

    hideFields();
    [100, 300, 500, 1000, 2000].forEach(d => setTimeout(hideFields, d));

    const checkout = document.querySelector('.wc-block-checkout');
    if (checkout) {
        const observer = new MutationObserver(() => {
            clearTimeout(window._spcbHideDebounce);
            window._spcbHideDebounce = setTimeout(hideFields, 50);
        });
        observer.observe(checkout, { childList: true, subtree: true });
        log('Duplicate field hide observer initialized');
    }
}

// Initialize immediately
hideDuplicateShippingFields();

// Also on DOMContentLoaded and load
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideDuplicateShippingFields);
}
window.addEventListener('load', hideDuplicateShippingFields);

